// @file Surface posts store
import window from 'bits/global'
import { getPostsByGroupByProperty } from 'bits/surface_posts_grouping_helper'
import { defineStore, storeToRefs } from 'pinia'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import type { Cid, Post, PostAttributes } from 'types'
import { computed, ref } from 'vue'

export const useSurfacePostsStore = defineStore('surfacePosts', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia

  const { sortedGroups, wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())

  const sortedPosts = computed<Post[]>(() => surfaceVuexStore?.getters['post/sortedPosts'] ?? [])
  const currentSortedPosts = computed(() => surfaceVuexStore?.getters['post/currentSortedPosts'] ?? [])
  const newestPostCid = computed(() => surfaceVuexStore?.getters['post/newestPostCid'] ?? [])
  const postsBySectionId = computed<{ [sectionId: number]: Post[] }>(
    () => surfaceVuexStore?.getters['post/postsBySectionId'] ?? {},
  )
  const postEntitiesByCid = computed<{ [postCid: string]: Post }>(
    () => surfaceVuexStore?.getters['post/postEntitiesByCid'] ?? {},
  )
  const postEntities = computed<Post[]>(() => Object.values(postEntitiesByCid.value))
  const sortedPostsCids = computed<string[]>(() => sortedPosts.value.map(({ cid }) => cid))
  const postCids = computed<string[]>(() => surfaceVuexStore?.getters['post/postCids'] ?? [])
  const postsByGroup = computed(() => {
    const sortedGroupIds = sortedGroups.value.map((group) => group.id)
    return getPostsByGroupByProperty(currentSortedPosts.value, sortedGroupIds, wishGroupBy.value)
  })
  const hasFetchedPosts = computed(() => surfaceVuexStore?.getters['post/isInitialPaginatedDataLoadDone'])

  const isASectionDragging = ref(false)
  function startNewPost({
    attributes,
    wishOrder,
  }: {
    attributes?: PostAttributes
    wishOrder?: { previousCid: Cid; nextCid: Cid }
  }): void {
    void surfaceVuexStore?.dispatch('post/startNewPost', { attributes, wishOrder })
  }

  function sortPost(payload: {
    postCid: Cid
    previousCid?: Cid | null
    nextCid?: Cid | null
    sortIndex?: number | null
    sectionId?: number | null
  }): void {
    void surfaceVuexStore?.dispatch('post/sortedPost', payload)
  }

  function changeDragging(payload: { isAPostDragging?: boolean; isASectionDragging?: boolean }): void {
    isASectionDragging.value = payload.isASectionDragging ?? false
    void surfaceVuexStore?.dispatch('changeDragging', { isAPostDragging: payload.isAPostDragging })
  }
  return {
    // state
    isASectionDragging,
    // getters
    sortedPostsCids,
    currentSortedPosts,
    newestPostCid,
    postsBySectionId,
    postEntitiesByCid,
    postEntities,
    postCids,
    postsByGroup,
    hasFetchedPosts,

    // actions
    startNewPost,
    sortPost,
    changeDragging,
  }
})
